import React from "react";
import { FiLayers, FiSmartphone, FiUser } from "react-icons/fi";
import {
  Code,
  Note,
  Section,
  Table,
  Tbody,
  TD,
  Thead,
} from "../../../../pages/KnowledgeCenterPage/ApiDocs/primitives";
import {
  Arrow,
  Diagram,
  Flow,
  GuideLink,
  Lead,
  List,
  MethodCell,
  Node,
  NodeItem,
  Step,
  Steps,
  TabBadge,
} from "../primitives";

/* ----------------------------------------------------------- method access */

const DEFAULTS: [string, string][] = [
  ["GET", "read"],
  ["POST", "write"],
  ["PUT", "write"],
  ["PATCH", "write"],
  ["DELETE", "admin"],
];

/** How Access Explorer shows a read-level role against the default method levels. */
const MethodExample: React.FC = () => (
  <figure aria-label="Method access example" className="rounded-lg border border-gray-200 bg-white">
    <div className="overflow-x-auto">
      <table className="w-full text-sm" style={{ minWidth: 560 }}>
        <thead className="bg-[#E7E4FF] text-left text-gray-700">
          <tr>
            <th className="px-3 py-2 font-semibold">Path</th>
            <th className="px-3 py-2 font-semibold">Role level</th>
            {DEFAULTS.map(([method]) => (
              <th key={method} className="px-3 py-2 font-semibold">{method}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr className="border-t border-gray-200">
            <td className="px-3 py-2 font-mono text-xs text-gray-800">/&lt;route&gt;/</td>
            <td className="px-3 py-2 text-gray-700">read</td>
            <td className="px-3 py-2"><MethodCell state="met" level="read" /></td>
            <td className="px-3 py-2"><MethodCell state="unmet" level="write" /></td>
            <td className="px-3 py-2"><MethodCell state="default" level="write" /></td>
            <td className="px-3 py-2"><MethodCell state="default" level="write" /></td>
            <td className="px-3 py-2"><MethodCell state="default" level="admin" /></td>
          </tr>
        </tbody>
      </table>
    </div>
    <figcaption className="border-t border-gray-200 px-3 py-2 text-xs leading-5 text-gray-500">
      Access Explorer, APIs tab. The role holds the group at read: GET passes, POST needs write. Gray italic
      means nothing is stored, so the default applies.
    </figcaption>
  </figure>
);

export const MethodAccess: React.FC = () => (
  <div className="space-y-8">
    <Lead>Use this when an API call gets a 403 it shouldn't, or when you add or edit an API permission object.</Lead>

    <Section title="A level per method">
      <p>Each API object has one level per method: GET, POST, PUT, PATCH and DELETE.</p>
      <Table minWidth={420}>
        <Thead cols={["Method", "Default for a new API object"]} />
        <Tbody>
          {DEFAULTS.map(([method, level]) => (
            <tr key={method}>
              <td className={TD}>{method}</td>
              <td className={TD}>{level}</td>
            </tr>
          ))}
        </Tbody>
      </Table>
      <p>
        In the Permission Objects form a blank level shows as "default (read)", "default (write)" and so on:
        nothing is stored and the default applies. The role's level on the endpoints group must be at least the
        method's level, where read &lt; write &lt; admin.
      </p>
      <p>
        A POST that only reads data should be <Code>read</Code>. This covers query-style POSTs, such as search or
        check-access bodies.
      </p>
      <MethodExample />
    </Section>

    <Section title="Stored paths have their ids removed">
      <p>
        Requests are matched on the exact path with ids removed. There is no prefix matching. Each run of id
        segments (numbers or <Code>{"{id}"}</Code> placeholders) becomes a single empty segment. A date segment
        such as <Code>2026-11-03</Code> becomes <Code>{"{date}"}</Code>.
      </p>
      <Table minWidth={560}>
        <Thead cols={["Route", "Store it as"]} />
        <Tbody>
          <tr>
            <td className={TD}><Code>/permission-object/{"{id}"}/method-access</Code></td>
            <td className={TD}><Code>/permission-object//method-access</Code></td>
          </tr>
          <tr>
            <td className={TD}><Code>/assets/{"{id}"}</Code> or <Code>/assets/123</Code></td>
            <td className={TD}><Code>/assets/</Code> (an id at the end leaves a single slash)</td>
          </tr>
          <tr>
            <td className={TD}><Code>/resources/{"{asset_id}"}/{"{resource_id}"}</Code></td>
            <td className={TD}><Code>/resources/</Code> (two ids in a row become one empty segment)</td>
          </tr>
        </Tbody>
      </Table>
      <p>
        A stored <Code>{"{id}"}</Code> matches nothing, and the caller gets a 403. When you type such a path,
        the Permission Objects form shows the stored form and a Use button that puts it in the Path field:
      </p>
      <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
        <div className="mb-1 text-xs font-medium text-gray-500">Path</div>
        <div className="rounded-lg border border-blue-500 px-3 py-1.5 font-mono text-xs text-gray-800">
          /permission-object/{"{id}"}/method-access
        </div>
        <p className="mt-1 text-xs text-amber-600">
          Requests are matched on the exact path with ids removed. Store this as{" "}
          <code>/permission-object//method-access</code>.
          <span className="ml-2 font-medium underline">
            Use <code>/permission-object//method-access</code>
          </span>
        </p>
      </div>
      <Note>
        If two permission objects share the same path and service, their method access is merged by path and
        the last one wins. Keep one object per path.
      </Note>
      <p>Saving with no methods is refused. Leave the defaults instead.</p>
    </Section>
  </div>
);

/* ------------------------------------------------------- FileCenter folders */

export const FileCenterFolders: React.FC = () => (
  <div className="space-y-8">
    <Lead>
      Use this when the Logic App can't upload for an asset, or a client can't see its folders in File Center.
    </Lead>

    <Section title="The Logic App's all-folders objects">
      <p>The Logic App and FileCenter uploads need two permission objects per asset:</p>
      <List>
        <li><Code>BLOB_ASSET_&lt;id&gt;_BETM_ALL_FOLDERS_WRITE</Code></li>
        <li><Code>BLOB_ASSET_&lt;id&gt;_BETM_ALL_FOLDERS_READ</Code></li>
      </List>
      <p>
        Both have type <Code>BLOB_FOLDER</Code>, path <Code>/_betm_all_folders_</Code> and service{" "}
        <Code>general_amp_service</Code>. They are stored lowercase in the database.
      </p>
      <Table minWidth={560}>
        <Thead cols={["Object", "Endpoints group"]} />
        <Tbody>
          <tr>
            <td className={TD}>WRITE</td>
            <td className={TD}><Code>betm_write_all_filecenter_blob_access</Code></td>
          </tr>
          <tr>
            <td className={TD}>READ</td>
            <td className={TD}><Code>betm_read_all_filecenter_blob_access</Code></td>
          </tr>
        </Tbody>
      </Table>
      <p>
        Grant folders on the Onboarding tab (Fix all runs it too) creates both objects if they are missing,
        re-enables them if they are disabled, and maps them. Running it twice changes nothing. See{" "}
        <GuideLink to="onboarding-an-asset">Onboarding an asset</GuideLink>.
      </p>
      <Note tone="info">
        The older "add folder" flow only maps a new folder to groups that already hold that asset's folders. For
        a brand-new asset it mapped to nothing. That is why the Onboarding grant exists.
      </Note>
    </Section>

    <Section title="A client's folders">
      <p>
        A client's folder access comes from its own FileCenter group: an endpoint group of BLOB_FOLDER objects for
        the client's assets, such as <Code>esvolta_all_blob_access</Code>. The client's role holds it at read.
      </p>
      <p>
        The folder pane on <TabBadge tab="Entitlements > Asset Group" /> (the row arrow on an asset, then Asset
        Folder Permissions) creates or deletes one folder object at a time. A new object goes only into
        FileCenter groups that already hold folders for that asset. For an asset the client's group doesn't
        cover yet, add its folder objects to the group on <TabBadge tab="Entitlements > Endpoints Group" />.
      </p>
    </Section>
  </div>
);

/* ------------------------------------------------------------ outage assets */

export const OutageAssets: React.FC = () => (
  <div className="space-y-6">
    <Lead>Use this when someone can't pick an asset in Outage Management, or a new asset should be reportable.</Lead>
    <List>
      <li>
        Outage Management's asset dropdown lists only the assets the caller's outage permission allows.
      </li>
      <li>
        Once the Outage-Assets group is set up, the <Code>betm-outage-management-user</Code> role uses the{" "}
        <Code>Outage-Assets</Code> asset group (plus <Code>BETM-Internal-Asset</Code>) instead of
        BETM-All-Assets. Admins and IT still see every asset.
      </li>
      <li>
        Upload-only accounts (assets that exist only so the Logic App can upload files) stay out of
        Outage-Assets.
      </li>
      <li>
        Only the dropdown is filtered. Existing outages on assets outside the group still show in the outage
        table.
      </li>
      <li>
        Add or remove an asset with the Outage reporting row on the Onboarding tab (Add to Outage-Assets or
        Remove from Outage-Assets), or on the Asset Group tab.
      </li>
    </List>
    <p>
      If the list cannot be loaded, the form shows "Couldn't load your outage asset list. Refresh the page or
      contact an admin." instead of the dropdown.
    </p>
  </div>
);

/* ------------------------------------------------------------- mobile users */

export const MobileDiagram: React.FC = () => (
  <Diagram label="Mobile access for one asset" minWidth={820}>
    <div
      className="grid items-center gap-y-4"
      style={{ gridTemplateColumns: "minmax(140px,1fr) 100px minmax(190px,1.2fr) 110px minmax(240px,1.4fr)" }}
    >
      <div className="row-span-2">
        <Node title="User" caption="User Setup tab" icon={FiUser} tone="people" />
      </div>
      <div className="row-span-2 self-stretch">
        <Arrow label="has" />
      </div>
      <div className="row-span-2 self-stretch py-2">
        <Node
          title="Mobile role"
          caption="Role tab, domain mobile"
          icon={FiSmartphone}
          tone="people"
          className="flex h-full flex-col justify-center"
        >
          <NodeItem name={<Code>mobileapp-&lt;asset&gt;-read</Code>} />
        </Node>
      </div>
      <div className="self-stretch">
        <Arrow label="holds at read" />
      </div>
      <Node title="Endpoints Group" caption="the mobile app's pages and APIs" icon={FiLayers} tone="endpoint">
        <NodeItem name={<Code>mobile_app_core</Code>} />
      </Node>
      <div className="self-stretch">
        <Arrow label="holds" />
      </div>
      <Node title="Asset Group" caption="one asset" icon={FiLayers} tone="asset">
        <NodeItem name={<Code>MobileApp_&lt;Asset&gt;_Read</Code>} />
      </Node>
    </div>
  </Diagram>
);

export const MobileUsers: React.FC = () => (
  <div className="space-y-8">
    <Lead>Use this when someone needs the mobile app, or a new asset should show in it.</Lead>

    <p>
      Mobile app access is the <Code>mobile</Code> domain. It is separate from AMP access: a client user can have
      AMP, mobile or both. Each asset has its own mobile role.
    </p>

    <MobileDiagram />

    <Table minWidth={640}>
      <Thead cols={["Piece", "Name", "Notes"]} />
      <Tbody>
        <tr>
          <td className={TD}>Role</td>
          <td className={TD}><Code>mobileapp-&lt;asset&gt;-read</Code></td>
          <td className={TD}>One per asset, domain mobile.</td>
        </tr>
        <tr>
          <td className={TD}>Endpoints group</td>
          <td className={TD}><Code>mobile_app_core</Code></td>
          <td className={TD}>Held at read. Mobile roles have no FileCenter group.</td>
        </tr>
        <tr>
          <td className={TD}>Asset group</td>
          <td className={TD}><Code>MobileApp_&lt;Asset&gt;_Read</Code></td>
          <td className={TD}>One asset each. These are asset groups, not endpoint groups.</td>
        </tr>
        <tr>
          <td className={TD}>BETM read-all</td>
          <td className={TD}><Code>betm-mobile-read-all</Code></td>
          <td className={TD}>BETM's own role, with the <Code>BETM_Mobile_App</Code> asset group.</td>
        </tr>
      </Tbody>
    </Table>

    <Section title="Give a user the mobile app">
      <p>
        On <TabBadge tab="Entitlements > User Setup" />, select the user, click Edit, tick the{" "}
        <Code>mobileapp-&lt;asset&gt;-read</Code> role for each of their assets and click Save.
      </p>
    </Section>

    <Section title="Add an asset to the mobile app">
      <Steps>
        <Step title="Create its asset group" tab="Entitlements > Asset Group">
          <p>
            Asset Group Name <Code>MobileApp_&lt;Asset&gt;_Read</Code>, Domain mobile, Select Assets: the one
            asset.
          </p>
        </Step>
        <Step title="Create its role" tab="Entitlements > Role">
          <p>
            Role Name <Code>mobileapp-&lt;asset&gt;-read</Code>, Domain mobile, Users: at least one, Asset Groups
            (Collections): the group from step 1, Permission Object Groups: <Code>mobile_app_core</Code>.
          </p>
        </Step>
      </Steps>
    </Section>

    <p>Refresh access clears both amp and mobile cache entries.</p>
  </div>
);

/* ------------------------------------------------------- when changes apply */

export const ChangeTimeline: React.FC = () => (
  <Flow
    label="When a change reaches users"
    steps={[
      {
        kicker: "You save",
        title: "Removals apply at once",
        detail: "Delete, disable or lower a level, and the affected people's cached access is cleared.",
        tone: "accent",
      },
      {
        kicker: "Adds and changes",
        title: "Not live yet",
        detail: "Anything else you added or changed waits for a refresh.",
        tone: "muted",
      },
      {
        kicker: "Up to 15 minutes",
        title: "Refresh entitlements",
        detail: "Click it at the top right of the Entitlements tab (admins only).",
      },
      {
        kicker: "Every hour at :06",
        title: "Hourly refresh",
        detail: "Applies anything not refreshed yet.",
      },
      {
        kicker: "Any time",
        title: "Refresh access",
        detail: "Clears one person's cache now, in User Setup (admins only).",
      },
    ]}
  />
);

export const WhenChangesApply: React.FC = () => (
  <div className="space-y-6">
    <Lead>Use this when a change you saved doesn't show for a user yet.</Lead>
    <p>Users' access is cached in Redis. A page load reads the cache, not the database.</p>
    <ChangeTimeline />
    <ol className="list-decimal space-y-2 pl-5">
      <li>
        You save. If you removed or reduced access (delete, disable, lower a level), the affected people's cached
        access is cleared at once. Their next request uses the new rules.
      </li>
      <li>Anything you added or changed otherwise is not live yet.</li>
      <li>
        Click Refresh entitlements at the top right of the Entitlements tab (admins only). It can take up to 15
        minutes.
      </li>
      <li>Or wait for the hourly refresh at :06.</li>
      <li>Refresh access in User Setup clears one person's cache now (admins only).</li>
    </ol>
    <Table minWidth={560}>
      <Thead cols={["Action", "Where", "Effect"]} />
      <Tbody>
        <tr>
          <td className={TD}>Refresh entitlements</td>
          <td className={TD}>Entitlements tab, top right (admins only)</td>
          <td className={TD}>
            Refreshes everyone's cached access, in every domain, in one run. It says "Entitlements may take up
            to 15 mins to be refreshed."
          </td>
        </tr>
        <tr>
          <td className={TD}>Refresh assets</td>
          <td className={TD}>Assets tab, top right (admins only)</td>
          <td className={TD}>
            Rebuilds the asset and resource details the AMP pages read. It says "Assets may take up to 15 mins to
            be refreshed."
          </td>
        </tr>
        <tr>
          <td className={TD}>Refresh access</td>
          <td className={TD}>User Setup, Access Explorer (user mode)</td>
          <td className={TD}>
            Clears one user's cached entries for amp and mobile. Their next page load reads fresh from the
            database. The user needs an Entra object id. It says "Cleared N cached entries."
          </td>
        </tr>
      </Tbody>
    </Table>
    <p>
      The hourly refresh doesn't rewrite a user who has no grants left. Removing their last role, disabling or
      deleting them clears their cache instead. See <GuideLink to="offboarding">Offboarding</GuideLink>.
    </p>
    <Section title="Asset and resource changes">
      <p>
        The AMP pages read asset and resource details that a separate job rebuilds every hour. A new or changed
        asset, resource or asset detail shows on those pages after that hourly asset refresh.
      </p>
      <p>
        To show it sooner, click Refresh assets at the top right of the Assets tab (admins only). It can take up
        to 15 minutes.
      </p>
    </Section>
  </div>
);

/* -------------------------------------------------------------- offboarding */

export const Offboarding: React.FC = () => (
  <div className="space-y-6">
    <Lead>Use this when someone leaves a client, or to review users who no longer use AMP.</Lead>
    <p>
      Disabling a user, deleting a user, or removing one of their roles clears their cached access at once. Their
      next request uses the new rules.
    </p>
    <Table minWidth={680}>
      <Thead cols={["To", "In User Setup", "What happens"]} />
      <Tbody>
        <tr>
          <td className={TD}>Stop all access, keep the user</td>
          <td className={TD}>Edit, set Enabled to N, Save</td>
          <td className={TD}>They keep their roles but get no pages, APIs or asset groups.</td>
        </tr>
        <tr>
          <td className={TD}>Remove one client or app</td>
          <td className={TD}>Edit, untick the role, Save</td>
          <td className={TD}>The role's access goes at once.</td>
        </tr>
        <tr>
          <td className={TD}>Remove the user</td>
          <td className={TD}>Edit, Delete (admins only)</td>
          <td className={TD}>The user is deleted and their cache is cleared.</td>
        </tr>
      </Tbody>
    </Table>
    <Section title="Inactive users">
      <p>
        Use the "Inactive 90+ days" filter in User Setup as the shortlist. Users with no activity at all count as
        inactive. Activity is usage (API calls by month). It is not a history of grant changes. Select a user to
        see their activity by month.
      </p>
      <p>
        If the button reads "Activity unavailable", the activity couldn't be loaded and the filter is off.
      </p>
    </Section>
  </div>
);
